import { AuthError } from '@cascade/core/auth';
import {
  getErrorReason,
  isErrorBody,
  RequestError,
} from '@cascade/core/errors';
import { isKeyOf } from '@cascade/core/utils';
import type { TAuthErrorReason } from '@cascade/core/auth';
import type { TErrorCode, TRequestErrorKind } from '@cascade/core/errors';
import type {
  TErrorMessageKey,
  TInterpolation,
  TInterpolations,
  TMessageKeyWithoutPlaceholders,
  TReasonKeys,
} from './I18n.types.ts';

const codeKeys = {
  BAD_REQUEST: 'error.server.badRequest',
  UNAUTHORIZED: 'error.server.unauthorized',
  FORBIDDEN: 'error.server.forbidden',
  NOT_FOUND: 'error.server.notFound',
  CONFLICT: 'error.server.conflict',
  VALIDATION_ERROR: 'error.server.validation',
  RATE_LIMITED: 'error.server.rateLimited',
  UNAVAILABLE_LEGAL_REASONS: 'error.server.legalRestriction',
  INTERNAL_ERROR: 'error.server.internal',
  network: 'error.request.network',
  timeout: 'error.request.timeout',
  aborted: 'error.request.aborted',
  decode: 'error.request.unreadableResponse',
  refused: 'error.socket.refused',
} satisfies Record<
  TErrorCode | Exclude<TRequestErrorKind, 'server'>,
  TErrorMessageKey
>;

const authReasonKeys: Record<TAuthErrorReason, TErrorMessageKey> = {
  busy: 'error.auth.actionInProgress',
  missing_email: 'error.auth.missingEmail',
  unsupported_domain: 'error.auth.unsupportedDomain',
  unsupported_provider: 'error.auth.unsupportedProvider',
  unexpected: 'error.unexpected',
};

const placeholderPattern = /\{(\w+)\}/g;

const isText = (part: TInterpolation) =>
  typeof part === 'string' || typeof part === 'number';

const getCodeKey = (code: string): TErrorMessageKey =>
  isKeyOf(codeKeys, code) ? codeKeys[code] : 'error.server.internal';

export const interpolate = (
  message: string,
  interpolations?: TInterpolations,
): string | TInterpolation[] => {
  if (!interpolations) {
    return message;
  }

  const tokens = message.split(placeholderPattern);

  const parts = tokens.map((token, index) => {
    const isPlaceholderName = index % 2 === 1;

    if (!isPlaceholderName) {
      return token;
    }

    return token in interpolations ? interpolations[token] : `{${token}}`;
  });

  return parts.every(isText) ? parts.join('') : parts;
};

export const getErrorMessageKey = (
  error: unknown,
  reasonKeys: TReasonKeys = {},
): TMessageKeyWithoutPlaceholders => {
  const reason = getErrorReason(error);

  if (isKeyOf(reasonKeys, reason)) {
    return reasonKeys[reason];
  }

  if (error instanceof AuthError) {
    return authReasonKeys[error.reason];
  }

  if (isErrorBody(error)) {
    return getCodeKey(error.code);
  }

  if (!(error instanceof RequestError)) {
    return 'error.unexpected';
  }

  const { detail } = error;

  if (detail.kind !== 'server') {
    return getCodeKey(detail.kind);
  }

  if (!detail.body) {
    return 'error.server.internal';
  }

  return getCodeKey(detail.body.code);
};
